import type { QueryResult } from "../../shared/types.js";
import {
  formatValue,
  formatForName,
  parseFormat,
  type FormatName,
} from "./format.js";

const NUMERIC_TYPE = /int|float|numeric|double|decimal|money/i;

/** A column is numeric if its pg type says so, or every value parses finite. */
function isNumericCol(dataType: string, values: unknown[]): boolean {
  if (NUMERIC_TYPE.test(dataType)) return true;
  let any = false;
  for (const v of values) {
    if (v == null || v === "") continue;
    if (!Number.isFinite(Number(v))) return false;
    any = true;
  }
  return any;
}

function renderCell(v: unknown, fmt: FormatName): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "object") return JSON.stringify(v);
  return formatValue(v, fmt);
}

/**
 * Query result table. Numeric columns are right-aligned and formatted: an
 * explicit `format="col:currency"` wins, otherwise the column name is used to
 * guess (revenue -> $, gp_pct -> %, …). Text columns pass through.
 */
export function ResultTable({
  result,
  format,
}: {
  result: QueryResult;
  format?: string;
}) {
  if (!result.rows.length) {
    return <div className="dw-empty">No rows.</div>;
  }
  const fmtOf = parseFormat(format);
  const cols = result.columns.map((c, j) => {
    const numeric = isNumericCol(
      c.dataType,
      result.rows.map((r) => r[j]),
    );
    const fmt: FormatName =
      fmtOf(c.name) ?? (numeric ? formatForName(c.name) : "text");
    return { name: c.name, dataType: c.dataType, numeric, fmt };
  });
  return (
    <div className="dw-table-wrap">
      <table className="dw-table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.name}
                title={c.dataType}
                className={c.numeric ? "dw-num" : undefined}
              >
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row, i) => (
            <tr key={i}>
              {row.map((v, j) => (
                <td key={j} className={cols[j].numeric ? "dw-num" : undefined}>
                  {renderCell(v, cols[j].fmt)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {result.truncated && (
        <div className="dw-truncated">
          Showing first {result.rows.length} rows (result truncated).
        </div>
      )}
    </div>
  );
}
